import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  Avatar,
  Button,
  Paper,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  fetchUpdateUser,
  fetchChangePassword,
  selectUserData,
} from "../redux/slices/auth";
import axios from "../axios";

export const Profile = () => {
  const dispatch = useDispatch();
  const userData = useSelector(selectUserData);
  const [activeTab, setActiveTab] = useState(0);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    setValue,
    reset: resetProfile,
    watch,
    formState: { errors: profileErrors },
  } = useForm();

  const avatarPreview = watch("avatarUrl");

  useEffect(() => {
    if (userData) {
      resetProfile({
        fullName: userData.fullName,
        email: userData.email,
        avatarUrl: userData.avatarUrl,
      });
    }
  }, [userData, resetProfile]);

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    watch: watchPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm();

  const handleFileUpload = async (event) => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append("image", file);

      const { data } = await axios.post("/upload", formData);
      const fullImageUrl = data.url; // Добавляем базовый URL
      setValue("avatarUrl", fullImageUrl);
    } catch (error) {
      alert("Помилка при завантаженні аватара");
    }
  };

  const onSubmitProfile = async (values) => {
    try {
      await dispatch(fetchUpdateUser(values)).unwrap();
      alert("Профіль успішно оновлено!");
    } catch (error) {
      alert(error.message || "Помилка під час оновлення профілю");
    }
  };

  const onSubmitPassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      alert("Паролі не збігаються");
      return;
    }

    try {
      await dispatch(
        fetchChangePassword({
          email: userData.email,
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        })
      ).unwrap();
      alert("Пароль успішно змінено");
      resetPassword();
    } catch (error) {
      alert(error.message || "Помилка при зміні пароля");
    }
  };

  if (!userData) {
    return (
      <Paper sx={{ p: 2, textAlign: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
      >
        <Tab label="Редагувати профіль" />
        <Tab label="Змінити пароль" />
      </Tabs>

      {activeTab === 0 && (
        <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <Avatar
              src={`${avatarPreview || userData.avatarUrl}?${Date.now()}` || ""}
              alt="Uploaded"
              sx={{ width: 100, height: 100, margin: "0 auto" }}
            />
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="avatar-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="avatar-upload">
              <Button variant="contained" component="span" sx={{ mt: 2 }}>
                Завантажити аватар
              </Button>
            </label>
          </div>

          <TextField
            fullWidth
            label="Повне ім'я"
            {...registerProfile("fullName", { required: "Обов'язкове поле" })}
            error={Boolean(profileErrors.fullName)}
            helperText={profileErrors.fullName?.message}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            {...registerProfile("email", { required: "Обов'язкове поле" })}
            error={Boolean(profileErrors.email)}
            helperText={profileErrors.email?.message}
            sx={{ mb: 2 }}
          />

          <Button type="submit" variant="contained" fullWidth>
            Зберегти зміни
          </Button>
        </form>
      )}

      {activeTab === 1 && (
        <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
          <TextField
            fullWidth
            label="Поточний пароль"
            type="password"
            {...registerPassword("currentPassword", {
              required: "Обов'язкове поле",
            })}
            error={Boolean(passwordErrors.currentPassword)}
            helperText={passwordErrors.currentPassword?.message}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Новий пароль"
            type="password"
            {...registerPassword("newPassword", {
              required: "Обов'язкове поле",
              minLength: {
                value: 5,
                message: "Мінімум 5 символів",
              },
            })}
            error={Boolean(passwordErrors.newPassword)}
            helperText={passwordErrors.newPassword?.message}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Підтвердьте пароль"
            type="password"
            {...registerPassword("confirmPassword", {
              required: "Обов'язкове поле",
              validate: (value) =>
                value === watchPassword("newPassword") ||
                "Паролі не збігаються",
            })}
            error={Boolean(passwordErrors.confirmPassword)}
            helperText={passwordErrors.confirmPassword?.message}
            sx={{ mb: 2 }}
          />

          <Button type="submit" variant="contained" fullWidth>
            Змінити пароль
          </Button>
        </form>
      )}
    </Paper>
  );
};
